import React, { useState, useMemo, useCallback, useRef } from 'react';

import { AgGridReact } from 'ag-grid-react';

import { themeMaterial } from 'ag-grid-community';

import {
  TextField,
  Box,
  Button,
  ButtonGroup,
  makeStyles,
  InputAdornment,
  IconButton,
  Modal,
} from '@material-ui/core';

import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';

const useStyles = makeStyles((theme) => ({
  searchTitle: {
    color: theme.palette.accent.darkgrey,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchBar: {
    borderRadius: 10,
    borderColor: theme.palette.accent.darkgrey,
  },
  tableContainer: {
    height: '500px',
    width: '100%',
  },
}));

export const defaultSortFunction = (
  valueA,
  valueB,
  nodeA,
  nodeB,
  isDescending
) => {
  // Sort numerically
  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  }

  // Sort strings lexicographically. Strings, iso dates.
  const aLower = String(valueA).toLowerCase();
  const bLower = String(valueB).toLowerCase();

  if (aLower < bLower) {
    return -1;
  }
  if (aLower > bLower) {
    return 1;
  }
  return 0;
};

const FCGrid = ({
  columnDefs,
  columnDefsFullScreen = null,
  search = false,
  ...props
}) => {
  const gridRef = useRef(null);
  const gridRefFS = useRef(null);
  const classes = useStyles();
  const [maximized, setMaximized] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [initialState, setInitialState] = useState();
  const [currentState, setCurrentState] = useState();

  const onGridPreDestroyed = useCallback((params) => {
    const { state } = params;
    localStorage.setItem('GridState-PendingAccounts', JSON.stringify(state));
    //setInitialState(state);
  }, []);

  const saturatedColumnDefs = columnDefs.map((col) => ({
    ...col,
    comparator:
      typeof col.comparator === 'function'
        ? col.comparator
        : defaultSortFunction,
  }));

  const saturatedFSColumnDefs = (columnDefsFullScreen || columnDefs).map(
    (col) => ({
      ...col,
      comparator:
        typeof col.comparator === 'function'
          ? col.comparator
          : defaultSortFunction,
    })
  );

  const onStateUpdated = useCallback((params) => {
    setCurrentState(params.state);
  }, []);

  const paginationPageSizeSelector = useMemo(() => [5, 10, 20, 50, 100], []);

  const autoSizeStrategy = useMemo(() => ({ type: 'fitGridWidth' }), []);

  const onDownloadCSV = useCallback(() => {
    gridRef.current?.api.exportDataAsCsv();
  }, []);

  const onDownloadCSVFS = useCallback(() => {
    gridRefFS.current?.api.exportDataAsCsv();
  }, []);

  const FCTheme = themeMaterial.withParams({
    headerTextColor: '#00B8D9',
    headerFontSize: '14px',
    accentColor: '#00B8D9',
    spacing: 5,
  });

  const searchLabel = 'Search';

  const toggleMaximized = () => {
    setMaximized((prev) => !prev);
  };

  const onFirstDataRendered = useCallback((params) => {
    params.api.sizeColumnsToFit();
  }, []);

  return (
    <Box>
      <Modal open={maximized} onClose={toggleMaximized}>
        <div
          style={{
            width: '98vw',
            height: '85vh',
            margin: '1vh auto',
            background: 'white',
          }}
        >
          <Box className={classes.searchTitle} style={{ padding: '15px' }}>
            {search && (
              <TextField
                className={classes.searchBar}
                label={searchLabel}
                // variant="filled"
                style={{ width: '255px' }}
                size="small"
                onChange={(e) => setTableSearch(e.target.value)}
                value={tableSearch}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" aria-label="search">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <ButtonGroup
              color="primary"
              aria-label="outlined primary button group"
            >
              <Button
                variant="outlined"
                startIcon={<FullscreenExitIcon />}
                onClick={toggleMaximized}
              >
                Exit Full Screen
              </Button>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={onDownloadCSVFS}
              >
                CSV
              </Button>
            </ButtonGroup>
          </Box>
          <div style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              ref={gridRefFS}
              theme={FCTheme}
              paginationPageSizeSelector={paginationPageSizeSelector}
              quickFilterText={tableSearch}
              initialState={initialState}
              onGridPreDestroyed={onGridPreDestroyed}
              onStateUpdated={onStateUpdated}
              columnDefs={saturatedFSColumnDefs}
              onFirstDataRendered={onFirstDataRendered}
              {...props}
            />
          </div>
        </div>
      </Modal>
      <Box className={classes.searchTitle}>
        {search && (
          <TextField
            className={classes.searchBar}
            label={searchLabel}
            // variant="filled"
            style={{ maxWidth: '255px', minWidth: '150px' }}
            size="small"
            onChange={(e) => setTableSearch(e.target.value)}
            value={tableSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton type="submit" aria-label="search">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
        <ButtonGroup color="primary" aria-label="outlined primary button group">
          <IconButton aria-label="toggle full screen" onClick={toggleMaximized}>
            <FullscreenIcon />
          </IconButton>
          <IconButton aria-label="download CSV" onClick={onDownloadCSV}>
            <GetAppIcon />
          </IconButton>
        </ButtonGroup>
      </Box>
      <Box className={classes.tableContainer}>
        <AgGridReact
          ref={gridRef}
          theme={FCTheme}
          paginationPageSizeSelector={paginationPageSizeSelector}
          quickFilterText={tableSearch}
          initialState={initialState}
          onGridPreDestroyed={onGridPreDestroyed}
          onStateUpdated={onStateUpdated}
          columnDefs={saturatedColumnDefs}
          onFirstDataRendered={onFirstDataRendered}
          {...props}
        />
      </Box>
    </Box>
  );
};

export default FCGrid;
